// The Beatmate scheduler + mixer - a TypeScript port of MetronomeEngine.cpp's
// audio callback. Runs inside an AudioWorkletProcessor (one instance per
// AudioContext) and, identically, inside unit tests against a plain frame
// counter. Rules inherited from the C++: no allocation in the hot loop except
// event records, no timers - the output stream is the clock, every event is a
// double-precision frame position compared against `framePos`.

import {
  ACCENT_PITCH, FADE_STEP, LEVEL_ACCENT, LEVEL_NORMAL, LEVEL_SECONDARY, MAX_BEATS, MAX_POLY_RINGS,
  MAX_POLY_STEPS, MAX_SAMPLE_SLOTS, MAX_SEQ_STEPS, MAX_SEQ_TRACKS, MAX_TICKS_PER_BEAT, MAX_VOICE_SECONDS,
  RVB_AP_GAIN, RVB_AP_MS, RVB_COMB_DAMP, RVB_COMB_FEEDBACK, RVB_COMB_MS, RVB_PREDELAY_MS, RVB_WET_SCALE,
  SEQ_CLIP_KNEE, SEQ_COMP_ATTACK_SEC, SEQ_COMP_MAKEUP_SHARE, SEQ_COMP_MAX_THRESH_DB, SEQ_COMP_RELEASE_SEC,
  SEQ_NORMAL_GAIN, SEQ_STEPS_PER_UNIT, SLOTS_PER_BEAT, VOICE_ACCENT, VOICE_BEAT, VOICE_SUB, tanhApprox,
} from './constants';
import { defaultStepPattern, stepStride } from './stepGrid';
import type { EngineEvent, EngineMode } from './protocol';

void ACCENT_PITCH; // documented in patch.ts (renderRoleTones); kept for parity

export interface ToneSet {
  accent: Float32Array;
  secondary: Float32Array;
  normal: Float32Array;
  sub: Float32Array;
  /** [ring * 2] normal, [ring * 2 + 1] accent. */
  poly: Float32Array[];
}

const EMPTY = new Float32Array(0);

function emptyTones(): ToneSet {
  return { accent: EMPTY, secondary: EMPTY, normal: EMPTY, sub: EMPTY, poly: new Array(MAX_POLY_RINGS * 2).fill(EMPTY) };
}

/** A poly ring's voice: one click + its own declick fade, so rings never choke each other. */
class Lane {
  active: Float32Array | null = null;
  pos = 0;
  len = 0;
  fading: Float32Array | null = null;
  fadePos = 0;
  fadeLen = 0;
  fadeGain = 0;
}

/** A sequencer track voice: plays the bank sample directly with a lane gain. */
class SeqLane {
  data: Float32Array | null = null;
  pos = 0;
  len = 0;
  gain = 1;
  fadeData: Float32Array | null = null;
  fadePos = 0;
  fadeLen = 0;
  fadeVol = 1;
  fadeGain = 0;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const nib = (arr: Uint8Array, b: number) => {
  const v = arr[b];
  return v < 1 ? 1 : v > MAX_TICKS_PER_BEAT ? MAX_TICKS_PER_BEAT : v;
};

export class MetronomeCore {
  readonly rate: number;

  // ---- control values (set between process() calls) ----
  /** Tempo of the per-beat length quantum: BPM in uniform meters, denominator notes/min in grouped ones. */
  unitsPerMinute = 100;
  volLeft = 1;
  volRight = 1;
  beatsPerMeasure = 4;
  /** Per-beat structure: length in units and clicks per beat (1..8 each). */
  beatUnits = new Uint8Array(MAX_BEATS).fill(1);
  beatTicks = new Uint8Array(MAX_BEATS).fill(1);
  /** 0.5 (straight) .. 0.75 (dotted feel), applied to even tick counts in pairs. */
  swing = 0.5;
  /** Count-in bars before the pattern; sampled at start(). */
  countInBars = 0;
  /** Gap trainer: gapPlayBars sounding then gapMuteBars silent, repeating. 0 either side = off. */
  gapPlayBars = 0;
  gapMuteBars = 0;
  /** Bars-mode session length; the engine silences itself after this many bars (0 = off). */
  stopAfterBars = 0;
  /** 16 beats x 8 positional slots, levels 0..3. */
  stepPattern = defaultStepPattern();
  /** Mixer gain per voice role (accent/beat/sub), 0..1.5. */
  levelGain = new Float32Array([1, 1, 1]);
  /** Set after a tempo/structure/swing change so the in-flight interval is recomputed. */
  rescheduleRequested = false;

  mode: EngineMode = 'standard';

  // ---- polyrhythm ----
  polyRingCount = 2;
  polyCycleBeats = 4;
  polyRingSteps = new Int32Array([4, 3, 5, 2]);
  polyRingLevels = new Uint8Array(MAX_POLY_RINGS * MAX_POLY_STEPS);
  polyRingGain = new Float32Array([1, 1, 1, 1]);

  // ---- step sequencer ----
  seqTrackCount = 0;
  seqTrackSteps = new Int32Array(MAX_SEQ_TRACKS).fill(16);
  seqTrackLevels = new Uint8Array(MAX_SEQ_TRACKS * MAX_SEQ_STEPS);
  seqTrackSample = new Int32Array(MAX_SEQ_TRACKS).fill(-1);
  seqTrackGain = new Float32Array(MAX_SEQ_TRACKS).fill(1);
  seqPunch = 0;
  seqRoom = 0;

  tones: ToneSet = emptyTones();
  samples: (Float32Array | null)[] = new Array(MAX_SAMPLE_SLOTS).fill(null);

  // ---- observable state ----
  playing = false;
  /** Monotonic frame counter - the clock. The worklet pins it to currentFrame. */
  framePos = 0;
  /** Events produced by process(); the owner drains this after each quantum. */
  events: EngineEvent[] = [];

  // ---- standard scheduler ----
  private nextTickFrame = 0;
  private lastTickFrame = 0;
  private beatInMeasure = 0;
  private tickInBeat = 0;
  private lastTickBeat = 0;
  private lastTickTick = 0;
  private lastWasCountIn = false;
  private countInBeatsLeft = 0;
  private barIndex = -1;
  private barMuted = false;
  private sessionDone = false;
  private modeLast = 0;

  // ---- main click voice (ping-pong scale copies + declick) ----
  private readonly voiceBuf: [Float32Array, Float32Array];
  private voiceWrite = 0;
  private active: Float32Array | null = null;
  private activeLen = 0;
  private samplePos = 0;
  private fading: Float32Array | null = null;
  private fadingLen = 0;
  private fadingPos = 0;
  private fadeGain = 0;

  // ---- poly lanes ----
  private readonly lanes: Lane[] = [];
  private readonly laneBuf: [Float32Array, Float32Array][] = [];
  private readonly laneWrite = new Int32Array(MAX_POLY_RINGS);
  private polyCycleStart = 0;
  private polyLastUpm = 0;
  private polyLastCycleBeats = 0;
  private readonly polyNextIdx = new Int32Array(MAX_POLY_RINGS);
  private readonly polyLastSteps = new Int32Array(MAX_POLY_RINGS);
  private polyCycleCount = 0;

  // ---- sequencer lanes (24 tracks + 1 preview) ----
  private readonly seqLanes: SeqLane[] = [];
  private seqNextFrame = 0;
  private seqGlobalStep = 0;

  // ---- mastering bus ----
  private compEnv = 0;
  private readonly rvbPre: Float32Array;
  private rvbPreIdx = 0;
  private readonly rvbComb: Float32Array[] = [];
  private readonly rvbCombIdx = new Int32Array(4);
  private readonly rvbCombLp = new Float32Array(4);
  private readonly rvbAp: Float32Array[] = [];
  private readonly rvbApIdx = new Int32Array(2);

  private previewKind: 'voice' | 'ring' | 'sample' | null = null;
  private previewIndex = 0;

  // Idle-skip windows: the last frame at which any poly lane / seq lane (plus
  // the reverb tail) can still be sounding, so the per-frame mixer only scans
  // lanes that can carry signal. Purely an optimization - same output.
  private polyBusyUntil = -1;
  private seqBusyUntil = -1;

  constructor(sampleRate: number) {
    this.rate = sampleRate;
    const voiceFrames = Math.ceil(sampleRate * MAX_VOICE_SECONDS);
    this.voiceBuf = [new Float32Array(voiceFrames), new Float32Array(voiceFrames)];
    for (let r = 0; r < MAX_POLY_RINGS; r++) {
      this.lanes.push(new Lane());
      this.laneBuf.push([new Float32Array(voiceFrames), new Float32Array(voiceFrames)]);
    }
    for (let t = 0; t <= MAX_SEQ_TRACKS; t++) this.seqLanes.push(new SeqLane());
    for (let r = 0; r < MAX_POLY_RINGS; r++) {
      this.polyRingLevels[r * MAX_POLY_STEPS] = LEVEL_ACCENT;
      for (let s = 1; s < MAX_POLY_STEPS; s++) this.polyRingLevels[r * MAX_POLY_STEPS + s] = LEVEL_NORMAL;
    }
    this.rvbPre = new Float32Array(Math.max(1, Math.round((RVB_PREDELAY_MS / 1000) * sampleRate)));
    for (const ms of RVB_COMB_MS) this.rvbComb.push(new Float32Array(Math.max(1, Math.round((ms / 1000) * sampleRate))));
    for (const ms of RVB_AP_MS) this.rvbAp.push(new Float32Array(Math.max(1, Math.round((ms / 1000) * sampleRate))));
  }

  private modeIndex(): number {
    return this.mode === 'seq' ? 2 : this.mode === 'poly' ? 1 : 0;
  }

  /** Starts playback from the current frame; count-in applies in standard mode only. */
  start(withCountIn: boolean): void {
    this.resetSchedule(withCountIn);
    this.playing = true;
  }

  /** Stops scheduling; sounding voices fade out over ~2 ms (no waveform cut). */
  stop(): void {
    this.playing = false;
    this.declickActive();
    this.active = null;
    this.activeLen = 0;
    for (const lane of this.lanes) {
      if (lane.active && lane.pos < lane.len) {
        lane.fading = lane.active; lane.fadeLen = lane.len; lane.fadePos = lane.pos; lane.fadeGain = 1;
      }
      lane.active = null; lane.len = 0;
    }
    for (const l of this.seqLanes) {
      if (l.data && l.pos < l.len) {
        l.fadeData = l.data; l.fadeLen = l.len; l.fadePos = l.pos; l.fadeVol = l.gain; l.fadeGain = 1;
      }
      l.data = null; l.len = 0;
    }
    this.polyBusyUntil = Math.max(this.polyBusyUntil, this.framePos + 256);
    this.seqBusyUntil = Math.max(this.seqBusyUntil, this.framePos + 256);
  }

  /** One-shot audition of a role voice, a poly ring's normal tone, or a bank sample. */
  preview(kind: 'voice' | 'ring' | 'sample', index: number): void {
    this.previewKind = kind;
    this.previewIndex = index;
  }

  private resetSchedule(withCountIn: boolean): void {
    const now = this.framePos;
    this.nextTickFrame = now;
    this.lastTickFrame = now;
    this.beatInMeasure = 0;
    this.tickInBeat = 0;
    this.samplePos = 0;
    this.active = null;
    this.activeLen = 0;
    this.fading = null;
    this.fadingLen = 0;
    this.fadingPos = 0;
    this.fadeGain = 0;
    // No count-in in poly or seq mode (their grids have no shared count-in meter).
    this.countInBeatsLeft =
      withCountIn && this.mode === 'standard' ? this.countInBars * Math.max(1, this.beatsPerMeasure) : 0;
    this.lastTickBeat = 0;
    this.lastTickTick = 0;
    this.lastWasCountIn = this.countInBeatsLeft > 0;
    this.barIndex = -1;
    this.barMuted = false;
    this.sessionDone = false;
    this.modeLast = this.modeIndex();
    this.polyCycleStart = now;
    this.polyLastUpm = 0;
    this.polyLastCycleBeats = 0;
    this.polyCycleCount = 0;
    for (let r = 0; r < MAX_POLY_RINGS; r++) {
      const lane = this.lanes[r];
      lane.active = null; lane.len = 0; lane.pos = 0; lane.fading = null; lane.fadeGain = 0;
      this.polyNextIdx[r] = 0;
      this.polyLastSteps[r] = 0;
    }
    this.seqNextFrame = now;
    this.seqGlobalStep = 0;
    for (const l of this.seqLanes) {
      l.data = null; l.len = 0; l.pos = 0; l.fadeData = null; l.fadeGain = 0;
    }
    // Mastering: a fresh start owes no tail from the previous run.
    this.compEnv = 0;
    for (const line of this.rvbComb) line.fill(0);
    for (const line of this.rvbAp) line.fill(0);
    this.rvbPre.fill(0);
    this.rvbCombLp.fill(0);
  }

  // ---- voice helpers ----

  /** If the previous click is still sounding, fade its tail over ~2 ms instead of cutting it. */
  private declickActive(): void {
    if (this.active !== null && this.samplePos < this.activeLen) {
      this.fading = this.active;
      this.fadingLen = this.activeLen;
      this.fadingPos = this.samplePos;
      this.fadeGain = 1;
    }
  }

  /**
   * Starts a click by scale-copying it into engine-owned storage: bakes the
   * mixer gain in so a gain change never modulates a sounding click, and
   * ping-pongs two buffers so the declick fade keeps valid samples to read.
   */
  private triggerVoice(tone: Float32Array, gain: number): void {
    this.declickActive();
    const buf = this.voiceBuf[this.voiceWrite];
    const n = Math.min(tone.length, buf.length);
    if (n === 0) {
      this.active = null; this.activeLen = 0; this.samplePos = 0;
      return;
    }
    for (let k = 0; k < n; k++) buf[k] = tone[k] * gain;
    this.voiceWrite ^= 1;
    this.active = buf;
    this.activeLen = n;
    this.samplePos = 0;
  }

  private triggerLane(r: number, tone: Float32Array, gain: number): void {
    const lane = this.lanes[r];
    if (lane.active !== null && lane.pos < lane.len) {
      lane.fading = lane.active; lane.fadeLen = lane.len; lane.fadePos = lane.pos; lane.fadeGain = 1;
    }
    const buf = this.laneBuf[r][this.laneWrite[r]];
    const n = Math.min(tone.length, buf.length);
    if (n === 0) {
      lane.active = null; lane.len = 0; lane.pos = 0;
      return;
    }
    for (let k = 0; k < n; k++) buf[k] = tone[k] * gain;
    this.laneWrite[r] ^= 1;
    lane.active = buf;
    lane.len = n;
    lane.pos = 0;
    this.polyBusyUntil = Math.max(this.polyBusyUntil, this.framePos + n + 128);
  }

  /** Sequencer lanes reference the bank buffer directly (drum tails run seconds). A retrigger chokes + declicks its own previous hit. */
  private triggerSeqLane(lane: number, sample: Float32Array, gain: number): void {
    const l = this.seqLanes[lane];
    if (l.data !== null && l.pos < l.len) {
      l.fadeData = l.data; l.fadeLen = l.len; l.fadePos = l.pos; l.fadeVol = l.gain; l.fadeGain = 1;
    }
    l.data = sample;
    l.len = sample.length;
    l.pos = 0;
    l.gain = gain;
    this.seqBusyUntil = Math.max(this.seqBusyUntil, this.framePos + sample.length + 128);
  }

  /**
   * Renders `n` frames of stereo output. Structure mirrors the C++ callback:
   * one coherent parameter snapshot, then a per-frame loop that fires the
   * active scheduler and mixes voices.
   */
  process(outL: Float32Array, outR: Float32Array, n: number): void {
    const vl = this.volLeft;
    const vr = this.volRight;
    const upm = Math.max(1, this.unitsPerMinute);
    const unitsA = this.beatUnits;
    const ticksA = this.beatTicks;
    const sw = clamp(this.swing, 0.5, 0.75);
    const measure = Math.max(1, this.beatsPerMeasure);
    const pat = this.stepPattern;
    const mode = this.modeIndex();
    const rate = this.rate;

    const framesPerUnit = (rate * 60) / upm;
    const beatUnitsOf = (b: number) => nib(unitsA, b);
    const tickUnitsOf = (b: number, t: number) => {
      const ticks = nib(ticksA, b);
      let d = beatUnitsOf(b) / ticks;
      if ((ticks & 1) === 0) d *= 2 * ((t & 1) === 0 ? sw : 1 - sw);
      return d;
    };
    const beatFrames = (b: number) => framesPerUnit * beatUnitsOf(b);
    const tickFrames = (b: number, t: number) => Math.max(1, framesPerUnit * tickUnitsOf(b, t));

    // Structure changes that landed mid-measure/mid-beat: clamp the playhead.
    if (this.beatInMeasure >= measure) this.beatInMeasure = 0;
    if (this.tickInBeat >= nib(ticksA, this.beatInMeasure)) this.tickInBeat = 0;

    // A tempo/structure/swing change mid-wait recomputes the in-flight
    // interval under the new parameters and only ever pulls the next tick
    // closer, so changes are heard promptly and never double-fire.
    if (this.rescheduleRequested) {
      this.rescheduleRequested = false;
      const newDur = this.lastWasCountIn
        ? beatFrames(this.lastTickBeat)
        : tickFrames(this.lastTickBeat, this.lastTickTick);
      const candidate = this.lastTickFrame + newDur;
      if (candidate < this.nextTickFrame) this.nextTickFrame = candidate;
    }

    const playing = this.playing;

    // Mode flip while running: re-anchor the incoming mode's schedule at "now".
    if (playing && mode !== this.modeLast) {
      this.modeLast = mode;
      if (mode !== 0) this.countInBeatsLeft = 0;
      if (mode === 1) {
        this.polyCycleStart = this.framePos;
        this.polyLastUpm = 0; // forces the anchor refresh below
        for (let r = 0; r < MAX_POLY_RINGS; r++) {
          this.polyNextIdx[r] = 0;
          this.polyLastSteps[r] = 0;
        }
      } else if (mode === 2) {
        this.seqNextFrame = this.framePos;
        this.seqGlobalStep = 0;
      } else {
        this.nextTickFrame = this.framePos;
        this.lastTickFrame = this.nextTickFrame;
        this.beatInMeasure = 0;
        this.tickInBeat = 0;
        this.lastWasCountIn = false;
      }
    }

    // ---- sequencer snapshot ----
    let seqTracks = 0;
    let seqStepFrames = 1;
    if (mode === 2 && playing) {
      seqTracks = clamp(this.seqTrackCount, 0, MAX_SEQ_TRACKS);
      seqStepFrames = Math.max(1, framesPerUnit / SEQ_STEPS_PER_UNIT);
    }

    // ---- mastering snapshot ----
    const punch = this.seqPunch;
    const room = this.seqRoom;
    const compOn = punch > 0.001;
    const roomOn = room > 0.001;
    let compThresh = 1;
    let compMakeup = 1;
    if (compOn) {
      const threshDb = SEQ_COMP_MAX_THRESH_DB * punch;
      compThresh = Math.pow(10, threshDb * 0.05);
      compMakeup = Math.pow(10, -threshDb * 0.05 * SEQ_COMP_MAKEUP_SHARE);
    }
    const compAtk = 1 - Math.exp(-1 / (SEQ_COMP_ATTACK_SEC * rate));
    const compRel = 1 - Math.exp(-1 / (SEQ_COMP_RELEASE_SEC * rate));

    // ---- poly snapshot + anchor upkeep ----
    let polyCycleFrames = 1;
    let polyRings = 0;
    const ringSteps = this.polyRingSteps;
    if (mode === 1 && playing) {
      const cb = clamp(this.polyCycleBeats, 0.25, 64);
      polyRings = clamp(this.polyRingCount, 1, MAX_POLY_RINGS);
      polyCycleFrames = Math.max(1, framesPerUnit * cb);
      // Tempo or cycle-length change: move the anchor so the current phase is preserved.
      if (this.polyLastUpm !== upm || this.polyLastCycleBeats !== cb) {
        if (this.polyLastUpm > 0) {
          const oldCycleFrames = Math.max(1, ((rate * 60) / this.polyLastUpm) * this.polyLastCycleBeats);
          let phase = (this.framePos - this.polyCycleStart) / oldCycleFrames;
          if (phase < 0) phase = 0;
          if (phase >= 1) phase = phase % 1;
          this.polyCycleStart = this.framePos - phase * polyCycleFrames;
        } else {
          this.polyCycleStart = this.framePos;
        }
        this.polyLastUpm = upm;
        this.polyLastCycleBeats = cb;
        for (let r = 0; r < MAX_POLY_RINGS; r++) this.polyLastSteps[r] = 0;
        this.events.push({ kind: 'polyAnchor', frame: this.framePos, cycleStart: this.polyCycleStart, cycleFrames: polyCycleFrames });
      }
      // A ring's step count changed (or the anchor moved): next index is the first grid point at/after the phase.
      for (let r = 0; r < polyRings; r++) {
        const steps = clamp(ringSteps[r], 1, MAX_POLY_STEPS);
        if (this.polyLastSteps[r] === steps) continue;
        this.polyLastSteps[r] = steps;
        const phase = (this.framePos - this.polyCycleStart) / polyCycleFrames;
        const next = Math.ceil(phase * steps - 1e-9);
        this.polyNextIdx[r] = next < 0 ? 0 : next;
      }
    }

    const tones = this.tones;

    for (let i = 0; i < n; i++) {
      const fp = this.framePos;

      // ---- sequencer scheduler ----
      // All tracks fire on the shared step clock; track t sounds step
      // (global % steps[t]), which is the whole polymeter.
      if (playing && mode === 2 && fp >= this.seqNextFrame) {
        const g = this.seqGlobalStep;
        for (let t = 0; t < seqTracks; t++) {
          const steps = clamp(this.seqTrackSteps[t], 1, MAX_SEQ_STEPS);
          const idx = g % steps;
          const level = this.seqTrackLevels[t * MAX_SEQ_STEPS + idx] & 3;
          const slot = this.seqTrackSample[t];
          const sample = slot >= 0 && slot < MAX_SAMPLE_SLOTS ? this.samples[slot] : null;
          if (level > 0 && sample !== null && sample.length > 0) {
            this.triggerSeqLane(t, sample, this.seqTrackGain[t] * (level >= 2 ? 1 : SEQ_NORMAL_GAIN));
          }
        }
        this.events.push({ kind: 'seqStep', frame: fp, globalStep: g });
        // 16th pairs swing like the standard mode's even tick counts.
        const dur = Math.max(1, seqStepFrames * 2 * ((g & 1) === 0 ? sw : 1 - sw));
        this.seqNextFrame += dur;
        if (this.seqNextFrame <= fp) this.seqNextFrame = fp + dur; // late: restart spacing from now
        this.seqGlobalStep = g + 1;
      }

      // ---- polyrhythm scheduler ----
      if (playing && mode === 1) {
        if (fp >= this.polyCycleStart + polyCycleFrames) {
          this.polyCycleStart += polyCycleFrames;
          if (fp >= this.polyCycleStart + polyCycleFrames) this.polyCycleStart = fp; // long stall: restart from now
          for (let r = 0; r < MAX_POLY_RINGS; r++) this.polyNextIdx[r] = 0;
          this.polyCycleCount++;
          this.events.push({ kind: 'polyCycle', frame: fp, cycle: this.polyCycleCount, cycleStart: this.polyCycleStart, cycleFrames: polyCycleFrames });
        }
        for (let r = 0; r < polyRings; r++) {
          const steps = clamp(ringSteps[r], 1, MAX_POLY_STEPS);
          const idx = this.polyNextIdx[r];
          if (idx >= steps) continue;
          const fire = this.polyCycleStart + (polyCycleFrames * idx) / steps;
          if (fp < fire) continue;
          const level = this.polyRingLevels[r * MAX_POLY_STEPS + idx] & 3;
          if (level > 0) {
            const tone = tones.poly[r * 2 + (level >= 2 ? 1 : 0)];
            if (tone.length > 0) this.triggerLane(r, tone, this.polyRingGain[r]);
          }
          // Mute: nothing fires, the previous tail rings out.
          this.events.push({ kind: 'polyStep', frame: fp, ring: r, step: idx, level });
          this.polyNextIdx[r] = idx + 1;
        }
      }

      // ---- standard scheduler ----
      if (playing && mode === 0 && fp >= this.nextTickFrame) {
        let durUnits: number;

        if (this.countInBeatsLeft > 0) {
          // Count-in beat: every beat clicks (accent on the bar start), pattern mutes ignored, no subdivisions.
          const b = this.beatInMeasure;
          if (b === 0) this.triggerVoice(tones.accent, this.levelGain[VOICE_ACCENT]);
          else this.triggerVoice(tones.normal, this.levelGain[VOICE_BEAT]);
          this.events.push({
            kind: 'tick', frame: fp, beat: b, tick: 0, slot: -1, level: b === 0 ? LEVEL_ACCENT : LEVEL_NORMAL,
            bar: 0, countIn: this.countInBeatsLeft, muted: false,
          });
          durUnits = beatUnitsOf(b);
          this.lastTickBeat = b;
          this.lastTickTick = 0;
          this.lastWasCountIn = true;
          this.countInBeatsLeft--;
          this.beatInMeasure = (b + 1) % measure;
          if (this.countInBeatsLeft === 0) { // hand off to bar 1
            this.beatInMeasure = 0;
            this.tickInBeat = 0;
          }
        } else {
          const b = this.beatInMeasure;
          const t = this.tickInBeat;
          const slot = b * SLOTS_PER_BEAT + t * stepStride(nib(unitsA, b), nib(ticksA, b));
          const level = pat[slot] & 3;

          // A new pattern bar: advance the counter and decide once whether the whole bar is silenced.
          if (b === 0 && t === 0) {
            this.barIndex++;
            if (this.stopAfterBars > 0 && this.barIndex >= this.stopAfterBars && !this.sessionDone) {
              this.sessionDone = true;
              this.events.push({ kind: 'sessionDone', frame: fp, bar: this.barIndex + 1 });
            }
            const gp = this.gapPlayBars;
            const gm = this.gapMuteBars;
            this.barMuted = gp > 0 && gm > 0 && this.barIndex % (gp + gm) >= gp;
          }
          // Silenced bars keep the full schedule and every counter (the UI
          // playhead runs on - the point of gap training) but trigger no voices.
          const silenced = this.barMuted || this.sessionDone;

          if (t === 0) {
            if (silenced) {
              // No click; the previous tail rings out.
            } else if (level === LEVEL_ACCENT) {
              this.triggerVoice(tones.accent, this.levelGain[VOICE_ACCENT]);
            } else if (level === LEVEL_SECONDARY) {
              this.triggerVoice(tones.secondary, this.levelGain[VOICE_ACCENT]);
            } else if (level === LEVEL_NORMAL) {
              this.triggerVoice(tones.normal, this.levelGain[VOICE_BEAT]);
            }
            // Mute: trigger nothing and let the previous click's tail ring out.
          } else if (!silenced) {
            // Subdivision tick: accents promote it to the beat tone, normal plays the lighter sub tone.
            if (level === LEVEL_ACCENT || level === LEVEL_SECONDARY) {
              this.triggerVoice(tones.normal, this.levelGain[VOICE_BEAT]);
            } else if (level === LEVEL_NORMAL) {
              this.triggerVoice(tones.sub, this.levelGain[VOICE_SUB]);
            }
          }

          this.events.push({
            kind: 'tick', frame: fp, beat: b, tick: t, slot, level, bar: this.barIndex + 1, countIn: 0, muted: silenced,
          });

          durUnits = tickUnitsOf(b, t);
          this.lastTickBeat = b;
          this.lastTickTick = t;
          this.lastWasCountIn = false;

          this.tickInBeat++;
          if (this.tickInBeat >= nib(ticksA, b)) {
            this.tickInBeat = 0;
            this.beatInMeasure = (b + 1) % measure;
          }
        }

        this.lastTickFrame = this.nextTickFrame;
        const dur = Math.max(1, durUnits * framesPerUnit);
        // Drift-free: accumulate in double, the fractional part carries.
        this.nextTickFrame += dur;
        if (this.nextTickFrame <= fp) this.nextTickFrame = fp + dur; // late tick: resync forward, never burst
      }

      // ---- one-shot previews ----
      if (this.previewKind !== null) {
        const kind = this.previewKind;
        const idx = this.previewIndex;
        this.previewKind = null;
        if (kind === 'voice') {
          const tone = idx === VOICE_ACCENT ? tones.accent : idx === VOICE_SUB ? tones.sub : tones.normal;
          if (tone.length) this.triggerVoice(tone, this.levelGain[clamp(idx, 0, 2)]);
        } else if (kind === 'ring') {
          const tone = tones.poly[clamp(idx, 0, MAX_POLY_RINGS - 1) * 2];
          if (tone.length) this.triggerVoice(tone, 1);
        } else if (idx >= 0 && idx < MAX_SAMPLE_SLOTS) {
          const sample = this.samples[idx];
          if (sample && sample.length) this.triggerSeqLane(MAX_SEQ_TRACKS, sample, 1);
        }
      }

      // ---- mix ----
      let s = 0;
      if (this.active !== null && this.samplePos < this.activeLen) s = this.active[this.samplePos++];
      if (this.fading !== null) {
        if (this.fadingPos < this.fadingLen && this.fadeGain > 0) {
          s += this.fading[this.fadingPos++] * this.fadeGain;
          this.fadeGain -= FADE_STEP;
        } else {
          this.fading = null;
        }
      }
      if (fp <= this.polyBusyUntil) {
        for (let r = 0; r < MAX_POLY_RINGS; r++) {
          const lane = this.lanes[r];
          if (lane.active !== null && lane.pos < lane.len) s += lane.active[lane.pos++];
          if (lane.fading !== null) {
            if (lane.fadePos < lane.fadeLen && lane.fadeGain > 0) {
              s += lane.fading[lane.fadePos++] * lane.fadeGain;
              lane.fadeGain -= FADE_STEP;
            } else {
              lane.fading = null;
            }
          }
        }
      }
      // The sequencer bus (and its mastering chain, whose reverb tail runs
      // ~0.5 s) is only computed while something on it can still sound.
      if (fp > this.seqBusyUntil + rate) {
        if (s > 1) s = 1;
        else if (s < -1) s = -1;
        outL[i] = s * vl;
        outR[i] = s * vr;
        this.framePos++;
        continue;
      }
      let bus = 0;
      for (let t = 0; t <= MAX_SEQ_TRACKS; t++) {
        const l = this.seqLanes[t];
        if (l.data !== null && l.pos < l.len) bus += l.data[l.pos++] * l.gain;
        if (l.fadeData !== null) {
          if (l.fadePos < l.fadeLen && l.fadeGain > 0) {
            bus += l.fadeData[l.fadePos++] * l.fadeVol * l.fadeGain;
            l.fadeGain -= FADE_STEP;
          } else {
            l.fadeData = null;
          }
        }
      }

      // ---- mastering: glue -> room -> soft limiter (sequencer bus only) ----
      if (compOn) {
        const mag = bus < 0 ? -bus : bus;
        this.compEnv += (mag > this.compEnv ? compAtk : compRel) * (mag - this.compEnv);
        if (this.compEnv > compThresh) bus *= Math.sqrt(compThresh / this.compEnv);
        bus *= compMakeup;
      }
      if (roomOn) {
        const pre = this.rvbPre[this.rvbPreIdx];
        this.rvbPre[this.rvbPreIdx] = bus;
        if (++this.rvbPreIdx >= this.rvbPre.length) this.rvbPreIdx = 0;
        const input = pre * 0.5 + ((fp & 1) ? 1e-12 : -1e-12);
        let wet = 0;
        for (let c = 0; c < 4; c++) {
          const line = this.rvbComb[c];
          const y = line[this.rvbCombIdx[c]];
          this.rvbCombLp[c] += RVB_COMB_DAMP * (y - this.rvbCombLp[c]);
          line[this.rvbCombIdx[c]] = input + this.rvbCombLp[c] * RVB_COMB_FEEDBACK;
          if (++this.rvbCombIdx[c] >= line.length) this.rvbCombIdx[c] = 0;
          wet += y;
        }
        for (let a = 0; a < 2; a++) {
          const line = this.rvbAp[a];
          const y = line[this.rvbApIdx[a]];
          line[this.rvbApIdx[a]] = wet + y * RVB_AP_GAIN;
          if (++this.rvbApIdx[a] >= line.length) this.rvbApIdx[a] = 0;
          wet = y - RVB_AP_GAIN * wet;
        }
        bus += wet * 0.25 * room * RVB_WET_SCALE;
      }
      {
        const mag = bus < 0 ? -bus : bus;
        if (mag > SEQ_CLIP_KNEE) {
          const range = 1 - SEQ_CLIP_KNEE;
          let t = (mag - SEQ_CLIP_KNEE) / range;
          if (t > 3) t = 3;
          const shaped = SEQ_CLIP_KNEE + range * tanhApprox(t);
          bus = bus < 0 ? -shaped : shaped;
        }
      }
      s += bus;

      if (s > 1) s = 1;
      else if (s < -1) s = -1;
      outL[i] = s * vl;
      outR[i] = s * vr;
      this.framePos++;
    }
  }
}
